import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PapiBadge,
  PapiUser,
  PapiDeviation,
  PapiEditorText,
} from '@wix/da-types-papi';

import { formatDateOfBirth } from './utils';
import ArtistInfoLine from './ArtistInfoLine';
import Badges from './Badges';
import UserInfoBullets from './UserInfoBullets';
import CollapsibleBio from './CollapsibleBio';
import SocialMediaList from './SocialMediaList';
import s from './AboutMeV2.scss';

// We're not using some of these fields. Keeping them around for backwards compatibility
interface Props {
  moduleId: number;
  isWide?: boolean;
  isEmpty?: boolean;
  withBio?: boolean;
  user: PapiUser;
  gender?: string;
  isCurrentUserProfileOwner: boolean;
  isArtist?: boolean;
  isOpenForCommissions?: boolean;
  hasActiveCommissions?: boolean;
  level?: number;
  specialty?: number;
  website?: string;
  websiteLabel?: string;
  dobDay?: number;
  dobMonth?: number;
  dobYear?: number;
  countryId?: number;
  countrycode?: string;
  deviantFor: number; // duration in seconds
  devidDeviation?: PapiDeviation;
  bio?: PapiEditorText;
  badges?: PapiBadge[];
  showBadges: boolean;
  isMobile?: boolean;
  interests?: {
    id: number;
    value: string;
  }[];
  socialLinks?: {
    id: number;
    value?: string;
  }[];
  onStopEditing: () => void;
  hasJoinedWithEclipse?: boolean;
  expandBio?: boolean;
}

const AboutMe: React.FC<Props> = ({
  socialLinks,
  dobYear,
  dobDay,
  dobMonth,
  countrycode,
  deviantFor,
  website,
  websiteLabel,
  level,
  specialty,
  gender,
  badges,
  showBadges,
  bio,
  isArtist,
  interests,
  user,
  expandBio,
}) => {
  const { t } = useTranslation();

  return (
    <div className={s['root']}>
      {isArtist && (
        <ArtistInfoLine
          level={level}
          specialty={specialty}
          className={s['info-line']}
        />
      )}
      <UserInfoBullets
        dateOfBirth={formatDateOfBirth(dobDay, dobMonth, dobYear)}
        countryCode={countrycode}
        deviantFor={deviantFor}
        gender={gender}
        website={website}
        websiteLabel={websiteLabel}
      />
      {socialLinks && socialLinks.length > 0 && (
        <SectionAndLabel label={t('widgets.aboutMe.followMe')}>
          <SocialMediaList socialLinks={socialLinks} user={user} />
        </SectionAndLabel>
      )}
      {showBadges && badges && (
        <SectionAndLabel label={t('widgets.aboutMe.myBadges')}>
          <Badges badges={badges} collapseMoreThan={6} />
        </SectionAndLabel>
      )}

      {bio && (
        <div>
          <SectionLabel>{t('widgets.aboutMe.edit.myBio')}</SectionLabel>
          <CollapsibleBio
            bio={bio}
            interests={interests}
            isInitiallyExpanded={expandBio}
          />
        </div>
      )}
    </div>
  );
};
AboutMe.displayName = 'AboutMe';

// Ideally this would be a heading, but since we don't know what level heading we need, we should should hold off.
const SectionLabel = ({ children }) => (
  <div className={s['section-label']}>{children}</div>
);

const SectionAndLabel = ({ label, children }) => (
  <div className={s['section']}>
    <SectionLabel>{label}</SectionLabel>
    {children}
  </div>
);

export default AboutMe;
